import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Container,
  Pagination,
  Spinner,
  Alert,
  Form,
  InputGroup,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../api';

function Drafts() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchDrafts = async (page = 1, searchTerm = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/drafts?page=${page}&limit=${itemsPerPage}&search=${encodeURIComponent(searchTerm)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache',
        },
      });
      const data = response.data;
      console.log('[Drafts] Raw API response:', data);
      if (!Array.isArray(data.drafts)) {
        throw new Error('Invalid API response format');
      }
      const sanitizedDrafts = data.drafts.map(draft => ({
        ...draft,
        images: draft.images && typeof draft.images === 'string' ? draft.images : '[]',
      }));
      console.log('[Drafts] Fetched drafts:', sanitizedDrafts);
      setDrafts(sanitizedDrafts);
      setTotalPages(data.totalPages || 1);
      if (sanitizedDrafts.length === 0 && searchTerm) {
        setError('No drafts found for the search term.');
      }
    } catch (error) {
      console.error('❌ Error fetching drafts:', error);
      setError('Failed to load drafts. Please try again.');
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts(currentPage, search);
  }, [currentPage, search]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;
    try {
      await api.delete(`/drafts/${id}`);
      alert('Draft deleted successfully!');
      fetchDrafts(currentPage, search);
    } catch (error) {
      console.error('❌ Error deleting draft:', error);
      alert(`Failed to delete draft: ${error.message}`);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    }
  };

  const handlePublish = async (id) => {
    if (!window.confirm('Are you sure you want to publish this draft?')) return;
    try {
      await api.post(`/drafts/${id}/publish`);
      alert('Draft published successfully!');
      fetchDrafts(currentPage, search);
    } catch (error) {
      console.error('❌ Error publishing draft:', error);
      alert(`Failed to publish draft: ${error.message}`);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    }
  };

  const handleEditClick = (id) => navigate(`/inventory/drafts/edit/${id}`);

  const handleAddDraft = () => navigate('/inventory/drafts/new');

  const getImageUrl = (image) => {
    if (!image) return "No Image";
    const baseUrl = process.env.REACT_APP_API_URL || "https://thornsdashboard.com";
    const normalizedImage = image.startsWith('/') ? image.slice(1) : image;
    const url = normalizedImage.startsWith("http") ? normalizedImage : `${baseUrl}/${normalizedImage}`;
    console.log('[Drafts] Image URL:', url);
    return url;
  };

  const parseImages = (images) => {
    try {
      const parsed = JSON.parse(images) || [];
      console.log('[Drafts] Parsed images:', parsed);
      return parsed;
    } catch (e) {
      console.warn('[Drafts] Invalid images JSON:', images, e);
      return [];
    }
  };

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold">Drafts</h2>
        <Button variant="primary" onClick={handleAddDraft}>
          Add New Draft
        </Button>
      </div>

      <Form className="mb-3">
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Search by SKU or Name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            disabled={loading}
          />
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setCurrentPage(1);
            }}
            disabled={loading}
          >
            Clear
          </Button>
        </InputGroup>
      </Form>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <Spinner animation="border" className="d-block mx-auto my-3" />}

      {!loading && drafts.length === 0 && !error && (
        <Alert variant="info">No drafts found{search ? ` for "${search}"` : ''}. Try a different search term or page.</Alert>
      )}

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>SKU</th>
            <th>Name</th>
            <th>Cost ($)</th>
            <th>Price ($)</th>
            <th>Stock</th>
            <th>Condition Notes</th>
            <th>Dimensions (in)</th>
            <th>Weight (lb / oz)</th>
            <th>Platform</th>
            <th>Bin</th>
            <th>Images</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {drafts.length > 0 ? (
            drafts.map((draft) => (
              <tr key={draft.id}>
                <td>{draft.sku || 'N/A'}</td>
                <td>{draft.name || 'No Name'}</td>
                <td>{draft.cost ? `$${parseFloat(draft.cost).toFixed(2)}` : 'N/A'}</td>
                <td>{draft.price ? `$${parseFloat(draft.price).toFixed(2)}` : 'N/A'}</td>
                <td>{draft.stock || '0'}</td>
                <td>{draft.condition_notes || 'N/A'}</td>
                <td>
                  {draft.length && draft.width && draft.height
                    ? `${parseFloat(draft.length).toFixed(2)}x${parseFloat(draft.width).toFixed(2)}x${parseFloat(draft.height).toFixed(2)}`
                    : 'N/A'}
                </td>
                <td>
  {draft.weight || draft.ounces
    ? `${draft.weight ? parseFloat(draft.weight).toFixed(2) : '0'} lb ${draft.ounces ? parseFloat(draft.ounces).toFixed(2) : '0'} oz`
    : 'N/A'}
</td>

                <td>{draft.platform || 'N/A'}</td>
                <td>{draft.binLocation || draft.bin_location || 'Not Assigned'}</td>
                <td>
                  {parseImages(draft.images).length > 0 ? (
                    parseImages(draft.images).map((img, index) => (
                      <img
                        key={index}
                        src={getImageUrl(img)}
                        alt={`Draft ${draft.sku}`}
                        width="50"
                        height="50"
                        style={{ objectFit: 'cover', marginRight: '5px' }}
                        onError={(e) => {
                          console.error('[Drafts] Image load error:', img);
                          e.target.src = 'https://via.placeholder.com/50?text=Error';
                        }}
                      />
                    ))
                  ) : (
                    'No Images'
                  )}
                </td>
                <td>
                  <Button
                    variant="warning"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEditClick(draft.id)}
                    disabled={loading}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    className="me-2"
                    onClick={() => handleDelete(draft.id)}
                    disabled={loading}
                  >
                    Delete
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handlePublish(draft.id)}
                    disabled={loading}
                  >
                    Publish
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            !loading && <tr><td colSpan="12" className="text-center">No drafts available.</td></tr>
          )}
        </tbody>
      </Table>

      <Pagination className="justify-content-center mt-3">
        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1 || loading} />
        <Pagination.Prev
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1 || loading}
        />
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
          .map((page, _, filtered) => (
            <React.Fragment key={page}>
              {filtered.indexOf(page) > 0 && filtered[filtered.indexOf(page) - 1] < page - 1 && (
                <Pagination.Ellipsis disabled />
              )}
              <Pagination.Item
                active={page === currentPage}
                onClick={() => setCurrentPage(page)}
                disabled={loading}
              >
                {page}
              </Pagination.Item>
            </React.Fragment>
          ))}
        <Pagination.Next
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages || loading}
        />
        <Pagination.Last
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages || loading}
        />
      </Pagination>
    </Container>
  );
}

export default Drafts;